import { useEffect, useMemo, useState } from 'react';
import { fetchTariffWorkbench } from '@/services/tariffWorkbench.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';

export default function TarifasTab({ data = {}, onChange }) {
  const { tenant, token } = useAuth();
  const [tarifaBaseId, setTarifaBaseId] = useState(data?.tarifaBaseId || '');
  const [descuentoPct, setDescuentoPct] = useState(data?.descuentoPct || '');
  const [recargoPct, setRecargoPct] = useState(data?.recargoPct || '');
  const [requiereConfirmacion, setRequiereConfirmacion] = useState(
    data?.requiereConfirmacion || false
  );
  const [prioridadDespacho, setPrioridadDespacho] = useState(data?.prioridadDespacho || false);
  const [prioridadEntrega, setPrioridadEntrega] = useState(data?.prioridadEntrega || false);
  const [observaciones, setObservaciones] = useState(data?.observaciones || '');
  const [listasTarifarias, setListasTarifarias] = useState([]);
  const [isLoadingTarifas, setIsLoadingTarifas] = useState(false);
  const [tarifasError, setTarifasError] = useState('');

  useEffect(() => {
    setTarifaBaseId(data?.tarifaBaseId || '');
    setDescuentoPct(data?.descuentoPct || '');
    setRecargoPct(data?.recargoPct || '');
    setRequiereConfirmacion(data?.requiereConfirmacion || false);
    setPrioridadDespacho(data?.prioridadDespacho || false);
    setPrioridadEntrega(data?.prioridadEntrega || false);
    setObservaciones(data?.observaciones || '');
  }, [data]);

  useEffect(() => {
    let mounted = true;

    const loadTariffLists = async () => {
      setIsLoadingTarifas(true);
      setTarifasError('');

      try {
        const payload = await fetchTariffWorkbench({ tenant, token });
        if (!mounted) return;
        setListasTarifarias(Array.isArray(payload?.listas) ? payload.listas : []);
      } catch (error) {
        if (!mounted) return;
        console.error('Error cargando listas tarifarias del motor nuevo:', error);
        setTarifasError(error.message || 'No se pudieron cargar las listas tarifarias.');
        setListasTarifarias([]);
      } finally {
        if (mounted) setIsLoadingTarifas(false);
      }
    };

    loadTariffLists();
    return () => {
      mounted = false;
    };
  }, [tenant, token]);

  const tarifaOptions = useMemo(
    () =>
      listasTarifarias
        .map((lista) => ({
          value: lista.versionActualId ? String(lista.versionActualId) : '',
          label: `${lista.codigo || 'LISTA'} · ${lista.nombre || 'Sin nombre'}${lista.versionActualNombre ? ` · ${lista.versionActualNombre}` : ''}`,
          versionLabel: `v${lista.versionActualNumero || '-'} ${lista.versionActualNombre || 'Sin versión vigente'}`.trim(),
        }))
        .filter((item) => item.value),
    [listasTarifarias],
  );

  const emitChange = (next = {}) => {
    onChange?.({
      tarifaBaseId,
      descuentoPct,
      recargoPct,
      requiereConfirmacion,
      prioridadDespacho,
      prioridadEntrega,
      observaciones,
      ...next,
    });
  };

  const handleTarifaBaseChange = (value) => {
    setTarifaBaseId(value);
    emitChange({ tarifaBaseId: value });
  };

  const handleDescuentoChange = (value) => {
    setDescuentoPct(value);
    emitChange({ descuentoPct: value });
  };

  const handleRecargoChange = (value) => {
    setRecargoPct(value);
    emitChange({ recargoPct: value });
  };

  const handlePrioridadChange = (campo, value) => {
    if (campo === 'despacho') {
      setPrioridadDespacho(value);
      emitChange({ prioridadDespacho: value });
    } else if (campo === 'entrega') {
      setPrioridadEntrega(value);
      emitChange({ prioridadEntrega: value });
    }
  };

  const handleConfirmacionChange = (value) => {
    setRequiereConfirmacion(value);
    emitChange({ requiereConfirmacion: value });
  };

  const handleObservacionesChange = (value) => {
    setObservaciones(value);
    emitChange({ observaciones: value });
  };

  return (
    <div className="space-y-6">
      {/* Tarifa Base */}
      <section className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] p-6">
        <h3 className="mb-4 text-base font-semibold text-[rgb(var(--fg))]">
          Tarifa Base
        </h3>

        <div className="max-w-md">
          <label
            htmlFor="tarifa-base"
            className="mb-1.5 block text-sm font-medium text-[rgb(var(--fg))]"
          >
            Lista de Tarifas
          </label>
          <select
            id="tarifa-base"
            value={tarifaBaseId}
            onChange={(e) => handleTarifaBaseChange(e.target.value)}
            disabled={isLoadingTarifas}
            className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm text-[rgb(var(--fg))] focus:border-[rgb(var(--ring))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          >
            <option value="">
              {isLoadingTarifas ? 'Cargando listas...' : 'Seleccionar lista / versión...'}
            </option>
            {tarifaOptions.map((tarifa) => (
              <option key={tarifa.value} value={tarifa.value}>
                {tarifa.label}
              </option>
            ))}
          </select>
          {tarifasError ? (
            <p className="mt-2 text-xs text-[rgb(var(--brand))]">{tarifasError}</p>
          ) : tarifaOptions.length > 0 ? (
            <p className="mt-2 text-xs text-[rgb(var(--muted))]">
              Se muestran listas y versiones vigentes del motor tarifario canónico.
            </p>
          ) : (
            <p className="mt-2 text-xs text-[rgb(var(--muted))]">
              No hay listas con versión vigente disponibles en el motor nuevo.
            </p>
          )}
        </div>
      </section>

      {/* Ajustes sobre Tarifa */}
      <section className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] p-6">
        <h3 className="mb-4 text-base font-semibold text-[rgb(var(--fg))]">
          Ajustes sobre Tarifa
        </h3>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label
              htmlFor="descuento"
              className="mb-1.5 block text-sm font-medium text-[rgb(var(--fg))]"
            >
              Descuento (%)
            </label>
            <input
              id="descuento"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={descuentoPct}
              onChange={(e) => handleDescuentoChange(e.target.value)}
              placeholder="0.00"
              className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted))] focus:border-[rgb(var(--ring))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            />
          </div>

          <div>
            <label
              htmlFor="recargo"
              className="mb-1.5 block text-sm font-medium text-[rgb(var(--fg))]"
            >
              Recargo (%)
            </label>
            <input
              id="recargo"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={recargoPct}
              onChange={(e) => handleRecargoChange(e.target.value)}
              placeholder="0.00"
              className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted))] focus:border-[rgb(var(--ring))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            />
          </div>
        </div>

        <p className="mt-3 text-xs text-[rgb(var(--muted))]">
          Se aplica sobre la tarifa base seleccionada
        </p>
      </section>

      {/* Prioridades Operativas */}
      <section className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] p-6">
        <h3 className="mb-4 text-base font-semibold text-[rgb(var(--fg))]">
          Prioridades Operativas
        </h3>

        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <input
              id="prioridad-despacho"
              type="checkbox"
              checked={prioridadDespacho}
              onChange={(e) => handlePrioridadChange('despacho', e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] text-[rgb(var(--ring))] focus:ring-2 focus:ring-[rgb(var(--ring))] focus:ring-offset-0"
            />
            <label htmlFor="prioridad-despacho" className="cursor-pointer text-sm text-[rgb(var(--fg))]">
              Prioridad en despacho
            </label>
          </div>

          <div className="flex items-start gap-2">
            <input
              id="prioridad-entrega"
              type="checkbox"
              checked={prioridadEntrega}
              onChange={(e) => handlePrioridadChange('entrega', e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] text-[rgb(var(--ring))] focus:ring-2 focus:ring-[rgb(var(--ring))] focus:ring-offset-0"
            />
            <label htmlFor="prioridad-entrega" className="cursor-pointer text-sm text-[rgb(var(--fg))]">
              Prioridad en entrega
            </label>
          </div>

          <div className="flex items-start gap-2">
            <input
              id="confirmacion-previa"
              type="checkbox"
              checked={requiereConfirmacion}
              onChange={(e) => handleConfirmacionChange(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] text-[rgb(var(--ring))] focus:ring-2 focus:ring-[rgb(var(--ring))] focus:ring-offset-0"
            />
            <label htmlFor="confirmacion-previa" className="cursor-pointer text-sm text-[rgb(var(--fg))]">
              Requiere confirmación previa de tarifa
            </label>
          </div>
        </div>
      </section>

      {/* Observaciones Tarifarias */}
      <section className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] p-6">
        <h3 className="mb-4 text-base font-semibold text-[rgb(var(--fg))]">
          Observaciones Tarifarias
        </h3>

        <textarea
          id="observaciones"
          value={observaciones}
          onChange={(e) => handleObservacionesChange(e.target.value)}
          rows={4}
          placeholder="Notas adicionales sobre la tarifa o condiciones especiales..."
          className="w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 py-2 text-sm text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted))] focus:border-[rgb(var(--ring))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
        />
      </section>
    </div>
  );
}
